import { z } from "zod";
import { themePreferenceSchema } from "./account.js";
import { effectivePermissionsSchema } from "./permissions.js";

// =============================================================================
// Auth schemas — request/response shapes shared by the BFF and the
// frontend's typed API client. The BFF's actual database types are richer
// (password_hash, is_active, etc.); these are the public projections that
// land in the browser.
// =============================================================================

export const userRoleSchema = z.enum(["super_admin", "admin", "user"]);
export type UserRoleWire = z.infer<typeof userRoleSchema>;

export const sessionUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  /** Optional callback phone (digits-only). Empty string when the user
   *  hasn't supplied one. Surfaced on the session payload so the trip
   *  form can auto-fill it without making a separate /account call. */
  phone: z.string(),
  role: userRoleSchema,
  department: z.string(),
  /** UI theme. Shipped on the session payload so the ThemeProvider
   *  has the value on the same bootstrap roundtrip that resolves
   *  auth — no second `/account` call, no flash-of-wrong-theme.
   *  `"unset"` tells the provider to open the first-time picker. */
  themePreference: themePreferenceSchema,
  /** This user's resolved permission flags (key → allowed), based on
   *  their role and the current permissions matrix. Lets the frontend
   *  show/hide gated routes and buttons without a second call. Optional
   *  so an older server (pre-permissions) still validates; the frontend
   *  treats a missing map as "no extra permissions". */
  permissions: effectivePermissionsSchema.optional(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const meResponseSchema = z.discriminatedUnion("authenticated", [
  z.object({ authenticated: z.literal(false) }),
  z.object({ authenticated: z.literal(true), user: sessionUserSchema }),
]);
export type MeResponse = z.infer<typeof meResponseSchema>;

export const loginRequestSchema = z.object({
  email: z.string().min(3).max(200),
  password: z.string().min(1).max(500),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const magicLinkRequestSchema = z.object({
  email: z.string().min(3).max(200),
});
export type MagicLinkRequest = z.infer<typeof magicLinkRequestSchema>;

export const magicLinkRequestResponseSchema = z.object({
  sent: z.literal(true),
});
export type MagicLinkRequestResponse = z.infer<typeof magicLinkRequestResponseSchema>;

export const magicLinkConsumeRequestSchema = z.object({
  token: z.string().min(10).max(200),
});
export type MagicLinkConsumeRequest = z.infer<typeof magicLinkConsumeRequestSchema>;

export const acceptInvitationRequestSchema = z.object({
  token: z.string().min(10).max(200),
  password: z.string().min(8).max(500),
  firstName: z.string().min(1).max(50),
  lastName: z.string().min(1).max(50),
  /** Optional callback phone — auto-fills the trip form's "Call back #"
   *  field on every booking the user submits later. Users can skip it
   *  here and add it from /account, or override per-booking. */
  phone: z
    .string()
    .max(40)
    .transform((v) => v.trim())
    .optional()
    .default(""),
});
export type AcceptInvitationRequest = z.infer<typeof acceptInvitationRequestSchema>;

/** /api/auth/login, /magic-link/consume, /invitation/accept all return
 *  the same authenticated-success shape: a user record + a Set-Cookie. */
export const authSuccessSchema = z.object({
  authenticated: z.literal(true),
  user: sessionUserSchema,
});
export type AuthSuccess = z.infer<typeof authSuccessSchema>;

export const logoutResponseSchema = z.object({
  signedOut: z.literal(true),
});
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;
